/**
 * Generic cross-tab messaging over BroadcastChannel, bridged to the in-tab
 * event bus.
 *
 * Posting on a channel delivers to every OTHER tab's channel instance for the
 * same name (BroadcastChannel never echoes a message back to the instance that
 * posted it), where it is re-emitted on the event bus as `eventName` so
 * components can react via `eventBusCallbacks`. The posting tab is expected to
 * have already updated its own state locally.
 */
import eventBus from 'source/lib/event-bus';

/**
 * Create a cross-tab channel that re-emits received messages onto the event bus.
 *
 * @param {string} channelName - The BroadcastChannel name (shared across tabs).
 * @param {string} eventName - The event bus event to emit on incoming messages.
 * @returns {{ init: () => void, broadcast: (payload: any) => void }}
 */
export function createCrossTabChannel(channelName, eventName) {
    /** @type {BroadcastChannel | null} */
    let channel = null;

    function getChannel() {
        if (channel || typeof BroadcastChannel === 'undefined') return channel;

        channel = new BroadcastChannel(channelName);
        channel.addEventListener('message', (event) => {
            eventBus.$emit(eventName, event.data);
        });

        return channel;
    }

    return {
        /**
         * Start listening for cross-tab messages. Call once at app startup so a
         * tab receives broadcasts even if it never posts one itself.
         */
        init() {
            getChannel();
        },

        /**
         * Post a message to other tabs.
         *
         * @param {any} payload - Structured-cloneable payload to send.
         */
        broadcast(payload) {
            getChannel()?.postMessage(payload);
        },
    };
}
